import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';

interface ApproveCompanyWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (initialAmount: number) => Promise<void>;
  loading?: boolean;
}

const ApproveCompanyWalletModal: React.FC<ApproveCompanyWalletModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false
}) => {
  const [initialAmount, setInitialAmount] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const amount = parseFloat(initialAmount);
    if (isNaN(amount) || amount <= 0) {
      return;
    }

    await onConfirm(amount);
  };

  const handleClose = () => {
    setInitialAmount('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 m-0 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-[#182233]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-white">Approve Company Wallet</h3>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            ×
          </button>
        </div>
        
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
            <div>
              <h4 className="text-sm font-medium text-green-800">Create Company PDA</h4>
              <p className="text-sm text-green-700 mt-1">
                This will create a new company PDA on the Solana blockchain with the specified initial amount.
                The admin wallet will sign the transaction.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Initial Amount (USD)
            </label>
            <input
              type="number"
              value={initialAmount}
              onChange={(e) => setInitialAmount(e.target.value)}
              className="w-full appearance-none rounded-md border-0 bg-[#567095]/20 px-3 py-2 placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#2777EF] sm:text-sm"
              placeholder="Enter initial amount (must be greater than 0)"
              min="0.01"
              step="0.01"
              required
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">This amount will be allocated to the company wallet</p>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-[#567095]/30 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#567095]/20"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !initialAmount || parseFloat(initialAmount) <= 0}
            >
              {loading ? 'Processing...' : 'Approve & Create PDA'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApproveCompanyWalletModal;
